import React, { useCallback, useEffect } from "react";
import styled from "styled-components";

import { updateMxHeader } from "@trailhead/mx-header";
import { trackEvent, updateStickyProps } from "@trailhead/analytics";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { useMxContext } from "@trailhead/mx-context";
import { AEClient, TwilioClient } from "@trailhead/chat";
import { useQueryParams } from "@trailhead/smart-setup-core";
import { breakpoints } from "@trailhead/asurion-ui";
import { useWindowDimensions } from "@trailhead/core-ui";
import { OzmoSelfServePanel } from "@trailhead/ozmo";

import {
  AppCTAVersionB,
  ChatPromptsCTA,
  ContentCards,
  IntroHeader,
  GenericCTA,
  SectionContainer,
} from ".";
import PhoneWithIcons from "../assets/PhoneWithIconsBlue.png";
import PhoneWithIconsDesktop from "../assets/PhoneWithIconsBlueDesktop.png";
import WomanWithPhone from "../assets/WomanWithPhone.png";
import WomanWithPhoneDesktop from "../assets/WomanWithPhoneDesktop.png";

const MainContainer = styled.div`
  position: relative;

  & * {
    -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
    -moz-box-sizing: border-box; /* Firefox, other Gecko */
    box-sizing: border-box; /* Opera/IE 8+ */
  }
`;

const GenericCTAsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-end;
  gap: 1rem;
  width: 100%;

  ${breakpoints.mdMax} {
    flex-direction: column;
    gap: 2rem;
  }
`;

export type ExpertSetupContentCardsProps = {
  pathfinderVersion: string | undefined;
  protectMobileHeader: string;
  protectMobileDescription: string;
  protectMobileButtonText: string;
  protectMobileButtonUrl?: string;
  callButtonPhoneNumber?: string;
  showAppCta?: boolean;
  showCallButton?: boolean;
  showOzmoSearchPanel?: boolean;
  chatClient?: typeof TwilioClient | typeof AEClient;
};

export const ExpertSetupContentCards: React.FC<
  ExpertSetupContentCardsProps
> = ({
  pathfinderVersion,
  protectMobileHeader,
  protectMobileDescription,
  protectMobileButtonText,
  callButtonPhoneNumber,
  protectMobileButtonUrl,
  showAppCta = true,
  showCallButton = false,
  showOzmoSearchPanel = false,
  chatClient = TwilioClient || AEClient,
}: ExpertSetupContentCardsProps) => {
  const { client } = useMxContext();
  const t = useSanitizedTranslation();
  updateStickyProps({ pathfinderFlow: true, pathfinderVersion });

  const showCallButtonParam =
    useQueryParams().get("show_call_option") === "true";
  showCallButton = showCallButtonParam || showCallButton;

  const hubUrl =  "https://my.asurion.com"

  const showContentCards = false;

  updateMxHeader({
    view: "service-view",
    title: t("Mobile Device Set Up"),
    homePath: hubUrl,
    client,
  });

  useEffect(() => {
    updateStickyProps({ route: "/setup" });
    trackEvent("expert_setup_page_load");
    trackEvent("Page_Loaded", {
      pageName: "Pathfinder_Page",
      pathfinderVariant: "LandingPage_H",
    });
  }, []);

  const onHeaderButtonClick = useCallback(() => {
    trackEvent("Pathfinder_Chat_Button_Clicked");
    chatClient.showMessaging();
    chatClient.openMessagingOverlay();
  }, [chatClient]);

  const { width } = useWindowDimensions();
  const isDesktop = width > 1080; // same as breakpoint in GenericCTA.tsx

  return (
    <MainContainer>
      <SectionContainer>
        <IntroHeader
          buttonText={"Chat with an expert"}
          buttonCallback={onHeaderButtonClick}
          buttonVariant="default"
          showCallButton={showCallButton}
          callButtonPhoneNumber={callButtonPhoneNumber}
        />
      </SectionContainer>
      <SectionContainer
        additionalProps={`${breakpoints.lg}{ padding: 0; }`}
        removeMaxWidth={true}
      >
        <ChatPromptsCTA chatClient={chatClient} />
      </SectionContainer>
      {showOzmoSearchPanel && (
        <SectionContainer justifyContent="start">
          <OzmoSelfServePanel />
        </SectionContainer>
      )}
      <SectionContainer
        additionalProps={`${breakpoints.md}{ padding-top: 0 !important; }`}
      >
        <GenericCTAsContainer>
          <GenericCTA
            title="Mobile device activation "
            noWrapTitle="and set up"
            subTitle="We’ll walk you through the steps to make sure everything works seamlessly."
            buttonText="Use the setup guide"
            imageSrc={isDesktop ? PhoneWithIconsDesktop : PhoneWithIcons}
          />
          <GenericCTA
            title={protectMobileHeader}
            subTitle={protectMobileDescription}
            buttonText={protectMobileButtonText}
            imageSrc={isDesktop ? WomanWithPhoneDesktop : WomanWithPhone}
            buttonUrl={protectMobileButtonUrl}
          />
        </GenericCTAsContainer>
      </SectionContainer>
      {showContentCards && !showOzmoSearchPanel && (
        <SectionContainer justifyContent="start">
          <ContentCards />
        </SectionContainer>
      )}
      {showAppCta && (
        <SectionContainer additionalProps="padding-top: 0 !important; padding-bottom: 0 !important;">
          <AppCTAVersionB />
        </SectionContainer>
      )}
    </MainContainer>
  );
};
